import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Printer, Download, QrCode, Stethoscope } from "lucide-react";
import { toast } from "sonner";

function QrTile({ title, subtitle, url, filename }) {
  const ref = useRef(null);
  const download = () => {
    const canvas = ref.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${filename}.png`;
    a.click();
  };
  return (
    <Card className="p-5 flex flex-col items-center text-center break-inside-avoid">
      <div className="font-bold text-slate-900">{title}</div>
      {subtitle ? <div className="text-xs text-slate-500 mb-3">{subtitle}</div> : <div className="mb-3" />}
      <div ref={ref} className="bg-white p-2 border border-slate-200 rounded-lg">
        <QRCodeCanvas value={url} size={196} level="M" includeMargin />
      </div>
      <div className="text-[10px] text-slate-400 mt-2 break-all max-w-[220px]">{url}</div>
      <Button variant="outline" size="sm" className="mt-3 no-print" onClick={download}>
        <Download size={13} className="mr-1" /> Download
      </Button>
    </Card>
  );
}

export default function KioskCodes() {
  const { user } = useAuth();
  const hid = user?.active_hospital_id || user?.hospital_id || "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/doctors");
        setDoctors(data || []);
      } catch (e) {
        toast.error(formatErr(e?.response?.data?.detail || e));
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;

  const walkInUrl = `${origin}/kiosk/${hid}`;

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-start justify-between gap-3 no-print">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Work Sans" }}>
            <QrCode size={22} /> Queue Registration QR Codes
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Patients scan to self-register into today's queue. Print the general code for the reception tablet,
            or a per-doctor code so scans go straight into that doctor's line.
          </p>
        </div>
        <Button onClick={() => window.print()} className="bg-blue-700 hover:bg-blue-800">
          <Printer size={14} className="mr-2" /> Print all
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <QrTile
          title="Walk-in Registration"
          subtitle="Any available doctor"
          url={walkInUrl}
          filename="kiosk-walkin"
        />
        {doctors.map((d) => (
          <QrTile
            key={d.id}
            title={d.name || d.full_name}
            subtitle={d.specialty || "Register to this doctor"}
            url={`${origin}/kiosk/${hid}/${d.id}`}
            filename={`kiosk-${(d.name || d.id).replace(/\s+/g, "-").toLowerCase()}`}
          />
        ))}
      </div>

      {doctors.length === 0 ? (
        <div className="mt-4 text-sm text-slate-500 flex items-center gap-2">
          <Stethoscope size={14} /> No doctors found — add doctors to show per-doctor codes.
        </div>
      ) : null}
    </div>
  );
}
